import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Paperclip, Upload, Loader2 } from 'lucide-react';
import { api } from '../../lib/api.js';

export function Field({ label, hint, children, id }) {
  return (
    <div className="field">
      {label && <label htmlFor={id}>{label}</label>}
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

export function Text({ id, value, onChange, ...rest }) {
  return <input id={id} className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

export function Area({ id, value, onChange, code = false, ...rest }) {
  return (
    <textarea
      id={id}
      className={`textarea${code ? ' textarea-code' : ''}`}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

/** Seznam prostých textů (sylabus, pravidla, odstavce). */
export function StringList({ value = [], onChange, placeholder, multiline = false, addLabel = 'Přidat položku' }) {
  const set = (index, text) => onChange(value.map((item, i) => (i === index ? text : item)));
  const remove = (index) => onChange(value.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const next = [...value];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="repeat">
      {value.map((item, index) => (
        <div className="repeat-item" key={index}>
          {multiline
            ? <Area value={item} onChange={(text) => set(index, text)} placeholder={placeholder} rows={4} />
            : <Text value={item} onChange={(text) => set(index, text)} placeholder={placeholder} />}
          <div className="admin-row-order">
            <button type="button" className="icon-btn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Posunout nahoru">
              <ChevronUp size={13} />
            </button>
            <button type="button" className="icon-btn" onClick={() => move(index, 1)} disabled={index === value.length - 1} aria-label="Posunout dolů">
              <ChevronDown size={13} />
            </button>
          </div>
          <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(index)} aria-label="Odebrat">
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-sm" onClick={() => onChange([...value, ''])}>
          <Plus size={14} /> {addLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Seznam objektů s pevnými poli. `fields` popisuje sloupce,
 * volitelný `pick` doplní k danému poli výběr z nahraných souborů.
 */
export function ObjectList({ value = [], onChange, fields, addLabel, empty, files, onNeedFiles, notify }) {
  const set = (index, key, next) =>
    onChange(value.map((item, i) => (i === index ? { ...item, [key]: next } : item)));
  const remove = (index) => onChange(value.filter((_, i) => i !== index));

  return (
    <div className="repeat">
      {value.map((item, index) => (
        <div className="repeat-item" key={index}>
          <div className="repeat-fields">
            {fields.map((field) => (
              field.type === 'select' ? (
                <select
                  key={field.key}
                  className="select"
                  value={item[field.key] ?? field.options[0].value}
                  onChange={(e) => set(index, field.key, e.target.value)}
                  aria-label={field.label}
                >
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <Area
                  key={field.key}
                  value={item[field.key]}
                  onChange={(text) => set(index, field.key, text)}
                  placeholder={field.label}
                  rows={4}
                  style={{ gridColumn: '1 / -1' }}
                />
              ) : field.pickable ? (
                <div key={field.key} style={{ display: 'flex', gap: '.4rem', minWidth: 0 }}>
                  <Text
                    value={item[field.key]}
                    onChange={(text) => set(index, field.key, text)}
                    placeholder={field.label}
                  />
                  <FilePicker files={files} onNeedFiles={onNeedFiles} onPick={(url) => set(index, field.key, url)} />
                  <UploadButton
                    notify={notify}
                    onNeedFiles={onNeedFiles}
                    onUploaded={(file) => set(index, field.key, file.url)}
                  />
                </div>
              ) : (
                <Text
                  key={field.key}
                  value={item[field.key]}
                  onChange={(text) => set(index, field.key, text)}
                  placeholder={field.label}
                />
              )
            ))}
          </div>
          <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(index)} aria-label="Odebrat">
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-sm" onClick={() => onChange([...value, { ...empty }])}>
          <Plus size={14} /> {addLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Nahrání souboru rovnou z formuláře hodiny — záznam se tak dá přidat na
 * jedno místo, bez okliky přes záložku Soubory.
 */
export function UploadButton({ onUploaded, onNeedFiles, notify }) {
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const send = async (list) => {
    const file = list?.[0];
    if (!file) return;

    const form = new FormData();
    form.append('files', file);

    setProgress(0);
    try {
      const [saved] = await api.admin.upload(form, setProgress);
      if (saved) {
        onUploaded(saved);
        notify?.(saved.status === 'queued'
          ? 'Nahráno. Záznam se převádí na MP4 — odkaz už můžete uložit, video naskočí, jakmile bude hotovo.'
          : 'Soubor nahrán.');
      }
      onNeedFiles?.();
    } catch (error) {
      notify?.(error.message, 'error');
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const busy = progress !== null;

  return (
    <>
      <button
        type="button"
        className="icon-btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label="Nahrát soubor z počítače"
        title={busy ? `Nahrávám… ${Math.round(progress * 100)} %` : 'Nahrát z počítače'}
      >
        {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />}
      </button>
      <input ref={inputRef} type="file" hidden onChange={(event) => send(event.target.files)} />
    </>
  );
}

/** Tlačítko se seznamem nahraných souborů — vyplní URL bez ručního psaní. */
export function FilePicker({ files, onNeedFiles, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const onEscape = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const toggle = () => {
    setOpen((current) => {
      if (!current) onNeedFiles?.();
      return !current;
    });
  };

  return (
    <div className="picker" ref={ref}>
      <button type="button" className="icon-btn" onClick={toggle} aria-label="Vybrat z nahraných souborů" aria-expanded={open}>
        <Paperclip size={15} />
      </button>
      {open && (
        <div className="picker-menu">
          {(files || []).length === 0 ? (
            <p className="picker-empty">Zatím nemáte nahrané žádné soubory.</p>
          ) : (
            files.map((file) => (
              <button
                key={file.id}
                type="button"
                className="picker-item"
                onClick={() => { onPick(file.url); setOpen(false); }}
              >
                {file.originalName}
                <small>
                  {file.status === 'processing' ? `Převádí se na MP4 · ${file.progress} %`
                    : file.status === 'queued' ? 'Čeká na převod'
                    : file.url}
                </small>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
